// Lazy-loaded Sentry initialization to reduce initial bundle size
// Sentry is ~22 KiB gzipped - deferring it improves LCP significantly

let sentryModule: typeof import("@sentry/react") | null = null;

export function initSentry() {
  // Defer Sentry loading until after first paint
  if (typeof window !== "undefined") {
    // Use requestIdleCallback or setTimeout as fallback
    const scheduleInit = window.requestIdleCallback || ((cb: () => void) => setTimeout(cb, 2000));
    scheduleInit(() => {
      loadSentry();
    });
  }
}

async function loadSentry() {
  try {
    const Sentry = await import("@sentry/react");
    sentryModule = Sentry;

    const dsn = "https://0a21bf0eac7c08040c55bd8f5c2c68c1@o4510828551602256.ingest.de.sentry.io/4510828551602256";

    Sentry.init({
      dsn,
      environment: import.meta.env.MODE,
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      ignoreErrors: [
        /chrome-extension/,
        /moz-extension/,
        "Network request failed",
        "Failed to fetch",
        "Load failed",
        "AbortError",
        "AuthSessionMissingError",
      ],
      beforeSend(event) {
        if (event.user) {
          delete event.user.ip_address;
        }
        return event;
      },
    });

    // Make test function available after load
    (window as unknown as Record<string, unknown>).testSentry = testSentryIntegration;
  } catch (e) {
    console.warn("Sentry failed to load:", e);
  }
}

function getSentry() {
  return sentryModule;
}

// Error boundary - re-exported after Sentry loads
export function getSentryErrorBoundary() {
  return getSentry()?.ErrorBoundary ?? null;
}

export function captureError(error: Error, context?: Record<string, unknown>) {
  const S = getSentry();
  if (S) {
    S.captureException(error, { extra: context });
  } else {
    console.error("[Sentry not loaded]", error, context);
  }
}

export function setUserContext(userId: string, email?: string) {
  const S = getSentry();
  S?.setUser({ id: userId, email });
}

export function setSubscriptionContext(plan: string | null, isSubscribed: boolean) {
  const S = getSentry();
  if (S) {
    S.setTag("subscription_plan", plan || "free");
    S.setTag("is_subscribed", String(isSubscribed));
  }
}

export function setFeatureContext(feature: string) {
  getSentry()?.setTag("feature", feature);
}

export function clearUserContext() {
  getSentry()?.setUser(null);
}

export function testSentryIntegration() {
  const S = getSentry();
  if (!S) {
    console.warn("Sentry not loaded yet. Try again in a few seconds.");
    return false;
  }
  try {
    throw new Error("Sentry test error - integration verification");
  } catch (error) {
    S.captureException(error, { tags: { test: "manual_verification" } });
    console.log("✅ Test error sent to Sentry!");
    return true;
  }
}
